import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert, Plus, X, Search, Filter, Loader2, Trash2, Edit3,
  AlertTriangle, CheckCircle2, Clock, ExternalLink, MessageSquare,
  User, UserPlus, ShieldCheck, FileText, Link as LinkIcon, ArrowUpRight,
  Sparkles, RefreshCw, Copy, ChevronRight, Users, Flag, Zap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  draft:         { label: 'Draft',        color: 'slate',   icon: FileText },
  submitted:     { label: 'Submitted',    color: 'cyan',    icon: ArrowUpRight },
  acknowledged:  { label: 'Acknowledged', color: 'violet',  icon: CheckCircle2 },
  escalated:     { label: 'Escalated',    color: 'amber',   icon: AlertTriangle },
  resolved:      { label: 'Resolved',     color: 'emerald', icon: CheckCircle2 },
  rejected:      { label: 'Rejected',     color: 'red',     icon: X },
};
const PRIORITY_META = {
  low:    { label: 'Low',    color: 'slate' },
  medium: { label: 'Medium', color: 'cyan' },
  high:   { label: 'High',   color: 'amber' },
  urgent: { label: 'Urgent', color: 'red' },
};
const ACCENT_CLASS = {
  slate:   'bg-white/[0.04] text-white/60 border-white/10',
  cyan:    'bg-[#111a1c]/60 text-[#00E5FF]/90 border-[#00E5FF]/20',
  violet:  'bg-[#1a1220]/60 text-[#c39be5]/90 border-[#8A2BE2]/20',
  amber:   'bg-[#1a1712]/60 text-amber-300/90 border-amber-500/20',
  emerald: 'bg-[#121a15]/60 text-emerald-300/90 border-emerald-500/20',
  red:     'bg-[#1a1212]/60 text-red-300/90 border-red-500/20',
};

const DMCAManagementPage = ({ getAuthHeaders, currentUser }) => {
  const isSuperadmin = currentUser?.role === 'superadmin';
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', priority: '', q: '' });
  const [selectedCase, setSelectedCase] = useState(null);
  const [creating, setCreating] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [tab, setTab] = useState('cases');

  const authHeaders = useCallback(() => getAuthHeaders(), [getAuthHeaders]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.priority) params.append('priority', filter.priority);
      if (filter.q) params.append('q', filter.q);
      const [c, s] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dmca/cases?${params}`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/dmca/stats`, { headers: authHeaders() }),
      ]);
      setCases(c.data || []);
      setStats(s.data || null);
    } catch (err) {
      if (err.response?.status === 403) toast.error('DMCA access required');
      else toast.error('Failed to load DMCA data');
    } finally {
      setLoading(false);
    }
  }, [filter, authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="space-y-6 pb-8" data-testid="dmca-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0A0A12] p-6">
        <div className="pointer-events-none absolute top-0 right-0 w-[400px] h-[200px] bg-[#8A2BE2]/[0.04] rounded-full blur-[120px] -translate-y-1/4 translate-x-1/4" />
        <div className="pointer-events-none absolute bottom-0 left-0 w-[300px] h-[180px] bg-red-500/[0.03] rounded-full blur-[100px] translate-y-1/4 -translate-x-1/4" />
        <div className="relative flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#8A2BE2]/[0.15] to-red-500/[0.1] border border-white/10 flex items-center justify-center"
              style={{ boxShadow: '0 0 30px rgba(138,43,226,0.1)' }}>
              <ShieldAlert className="w-4 h-4 text-[#c39be5]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-medium">Compliance · Confidential</p>
              <h1 className="text-2xl font-light text-white tracking-tighter">DMCA <span className="bg-gradient-to-r from-[#c39be5] to-red-300 bg-clip-text text-transparent font-medium">Command</span></h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => setCreating(true)} data-testid="new-case-btn"
              className="h-10 px-4 rounded-full text-[11px] tracking-[0.1em] uppercase font-medium bg-gradient-to-br from-[#8A2BE2]/15 to-red-500/10 text-[#c39be5] border border-[#8A2BE2]/25 hover:from-[#8A2BE2]/25">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Case
            </Button>
            <Button onClick={() => setTab(tab === 'templates' ? 'cases' : 'templates')} data-testid="toggle-templates-btn"
              className={`h-10 px-4 rounded-full text-[11px] tracking-[0.1em] uppercase font-medium border ${tab === 'templates' ? 'bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/25' : 'bg-white/[0.03] text-white/70 border-white/[0.1] hover:bg-white/[0.06]'}`}>
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Templates
            </Button>
            {isSuperadmin && (
              <Button onClick={() => setAccessOpen(true)} data-testid="manage-access-btn"
                className="h-10 px-4 rounded-full text-[11px] tracking-[0.1em] uppercase font-medium bg-white/[0.03] text-white/70 border border-white/[0.1] hover:bg-white/[0.06]">
                <Users className="w-3.5 h-3.5 mr-1.5" /> Access
              </Button>
            )}
            <Button onClick={fetchAll} variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-full text-white/40 hover:text-white">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Templates panel (toggle) */}
      {tab === 'templates' && <TemplatesPanel authHeaders={authHeaders} isSuperadmin={isSuperadmin} />}

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total cases', value: stats.total, icon: FileText, color: 'cyan' },
            { label: 'Open', value: stats.open, icon: Zap, color: 'amber' },
            { label: 'Resolved', value: stats.by_status?.resolved || 0, icon: CheckCircle2, color: 'emerald' },
            { label: 'Urgent', value: stats.by_priority?.urgent || 0, icon: Flag, color: 'red' },
          ].map((s, i) => (
            <div key={i} className={`relative rounded-xl border p-4 ${ACCENT_CLASS[s.color]}`} data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
              <div className="flex items-center justify-between mb-1.5">
                <s.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="text-[9px] tracking-[0.15em] uppercase text-white/30">{s.label}</span>
              </div>
              <p className="text-white text-2xl font-light tabular-nums tracking-tight">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <input type="text" placeholder="Search title, offender, URL…" value={filter.q}
            onChange={(e) => setFilter(f => ({ ...f, q: e.target.value }))}
            data-testid="dmca-search"
            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-full pl-9 pr-4 py-2 text-white text-xs placeholder-white/30 focus:outline-none focus:border-[#8A2BE2]/30" />
        </div>
        <select value={filter.status} onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}
          className="bg-white/[0.03] border border-white/[0.08] rounded-full px-3 py-2 text-white text-xs focus:outline-none">
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filter.priority} onChange={(e) => setFilter(f => ({ ...f, priority: e.target.value }))}
          className="bg-white/[0.03] border border-white/[0.08] rounded-full px-3 py-2 text-white text-xs focus:outline-none">
          <option value="">All priorities</option>
          {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Cases list */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#c39be5] animate-spin" /></div>
      ) : cases.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-white/[0.06] bg-white/[0.01]">
          <ShieldCheck className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-white/50 text-sm">No cases yet.</p>
          <p className="text-white/30 text-xs mt-1">Create your first takedown to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {cases.map(c => (
            <button key={c.id} onClick={() => setSelectedCase(c)} data-testid={`case-${c.id}`}
              className="w-full text-left rounded-xl border border-white/[0.06] bg-[#0A0A12]/60 backdrop-blur-md p-4 hover:border-[#8A2BE2]/25 hover:bg-white/[0.02] transition-all group">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-white font-medium tracking-tight">{c.title}</h3>
                    <StatusPill status={c.status} />
                    <PriorityPill priority={c.priority} />
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-white/40">
                    {c.offender && <span className="flex items-center gap-1"><User className="w-2.5 h-2.5" /> {c.offender}</span>}
                    {c.platform && <span>· {c.platform}</span>}
                    <span>· {new Date(c.created_at).toLocaleDateString()}</span>
                    <span>· by {c.created_by_name || c.created_by}</span>
                  </div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-white/20 group-hover:text-white/50 mt-1 shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && <CreateCaseModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); fetchAll(); }} authHeaders={authHeaders} />}
      {selectedCase && <CaseDetailModal caseId={selectedCase.id} onClose={() => setSelectedCase(null)} onChanged={fetchAll} authHeaders={authHeaders} currentUser={currentUser} />}
      {accessOpen && isSuperadmin && <AccessModal onClose={() => setAccessOpen(false)} authHeaders={authHeaders} />}
    </div>
  );
};

// ─── Sub components ───
const StatusPill = ({ status }) => {
  const s = STATUS_META[status] || STATUS_META.draft;
  const I = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] tracking-[0.08em] font-medium border ${ACCENT_CLASS[s.color]}`}>
      <I className="w-2.5 h-2.5" strokeWidth={2} />
      {s.label}
    </span>
  );
};

const PriorityPill = ({ priority }) => {
  const p = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] tracking-[0.08em] font-medium border ${ACCENT_CLASS[p.color]}`}>
      <span className="w-1 h-1 rounded-full bg-current" />
      {p.label}
    </span>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium block mb-1.5">{label}</label>
    {children}
  </div>
);
const INPUT_CLS = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#8A2BE2]/30";

const CreateCaseModal = ({ onClose, onCreated, authHeaders }) => {
  const [form, setForm] = useState({ title: '', offender: '', offending_url: '', original_work: '', original_url: '', description: '', priority: 'medium', platform: 'Roblox', evidence_urls: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, evidence_urls: form.evidence_urls.split(/\r?\n/).map(s => s.trim()).filter(Boolean) };
      await axios.post(`${BACKEND_URL}/api/dmca/cases`, payload, { headers: authHeaders() });
      toast.success('Case created');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); } finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose} title="New DMCA Case" testid="create-case-modal">
      <div className="space-y-3">
        <Field label="Title *"><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className={INPUT_CLS} data-testid="case-title" placeholder="e.g. Stolen logo used at roblox.com/games/…" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Priority"><select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className={INPUT_CLS}>{Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></Field>
          <Field label="Platform"><input value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))} className={INPUT_CLS} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Offender"><input value={form.offender} onChange={e => setForm(f => ({ ...f, offender: e.target.value }))} className={INPUT_CLS} placeholder="Roblox username / handle" /></Field>
          <Field label="Offending URL"><input value={form.offending_url} onChange={e => setForm(f => ({ ...f, offending_url: e.target.value }))} className={INPUT_CLS} placeholder="https://…" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Original work"><input value={form.original_work} onChange={e => setForm(f => ({ ...f, original_work: e.target.value }))} className={INPUT_CLS} placeholder="Our stolen asset" /></Field>
          <Field label="Original URL"><input value={form.original_url} onChange={e => setForm(f => ({ ...f, original_url: e.target.value }))} className={INPUT_CLS} placeholder="https://starnightholdings.uk/…" /></Field>
        </div>
        <Field label="Evidence URLs (one per line)"><textarea value={form.evidence_urls} onChange={e => setForm(f => ({ ...f, evidence_urls: e.target.value }))} rows={3} className={`${INPUT_CLS} resize-none`} placeholder="https://…screenshot1\nhttps://…screenshot2" /></Field>
        <Field label="Description"><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} className={`${INPUT_CLS} resize-none`} /></Field>
        <div className="flex gap-2 justify-end pt-3 border-t border-white/[0.06]">
          <Button variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-case-btn"
            className="bg-gradient-to-br from-[#8A2BE2]/25 to-red-500/15 text-[#c39be5] border border-[#8A2BE2]/30 px-5 rounded-full text-[11px] tracking-[0.1em] uppercase">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-2" />}
            File case
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const CaseDetailModal = ({ caseId, onClose, onChanged, authHeaders, currentUser }) => {
  const [caseData, setCaseData] = useState(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/dmca/cases/${caseId}`, { headers: authHeaders() });
      setCaseData(r.data);
    } catch { toast.error('Load failed'); } finally { setLoading(false); }
  }, [caseId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/dmca/cases/${caseId}`, { status }, { headers: authHeaders() });
      toast.success(`Marked ${STATUS_META[status]?.label || status}`);
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/dmca/cases/${caseId}/notes`, { text: note }, { headers: authHeaders() });
      setNote(''); load();
    } catch { toast.error('Add note failed'); }
  };

  const doDelete = async () => {
    if (!window.confirm('Delete this case permanently?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/dmca/cases/${caseId}`, { headers: authHeaders() });
      toast.success('Case deleted'); onChanged(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (loading || !caseData) return <Modal onClose={onClose} title="Loading…" testid="case-detail-modal"><Loader2 className="w-5 h-5 animate-spin text-cyan-400 mx-auto" /></Modal>;

  return (
    <Modal onClose={onClose} title={caseData.title} subtitle="Case detail" testid="case-detail-modal">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <StatusPill status={caseData.status} />
        <PriorityPill priority={caseData.priority} />
        {caseData.platform && <span className="text-[10px] tracking-[0.1em] uppercase text-white/40">· {caseData.platform}</span>}
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-5 text-xs">
        {caseData.offender && <InfoRow label="Offender" value={caseData.offender} />}
        {caseData.offending_url && <InfoRow label="Offending URL" value={caseData.offending_url} link />}
        {caseData.original_work && <InfoRow label="Original work" value={caseData.original_work} />}
        {caseData.original_url && <InfoRow label="Original URL" value={caseData.original_url} link />}
      </div>

      {caseData.description && <div className="mb-5">
        <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-1.5">Description</p>
        <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap">{caseData.description}</p>
      </div>}

      {caseData.evidence_urls?.length > 0 && <div className="mb-5">
        <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-1.5">Evidence</p>
        <ul className="space-y-1">
          {caseData.evidence_urls.map((u, i) => (
            <li key={i}><a href={u} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00E5FF]/80 hover:underline break-all flex items-center gap-1.5">
              <LinkIcon className="w-2.5 h-2.5" /> {u}
            </a></li>
          ))}
        </ul>
      </div>}

      {/* Status actions */}
      <div className="mb-5">
        <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-2">Move to status</p>
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(STATUS_META).filter(s => s !== caseData.status).map(s => (
            <button key={s} onClick={() => updateStatus(s)} data-testid={`status-${s}`}
              className={`px-2.5 py-1 rounded-md text-[10px] tracking-[0.05em] font-medium border ${ACCENT_CLASS[STATUS_META[s].color]} hover:brightness-125`}>
              {STATUS_META[s].label}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="mb-3">
        <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-2">Notes ({(caseData.notes || []).length})</p>
        <div className="space-y-2 max-h-52 overflow-y-auto mb-2">
          {(caseData.notes || []).map(n => (
            <div key={n.id} className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-white/50 font-medium">{n.by_name || n.by}</span>
                <span className="text-[10px] text-white/30">{new Date(n.at).toLocaleString()}</span>
              </div>
              <p className="text-xs text-white/70 leading-relaxed whitespace-pre-wrap">{n.text}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-1.5">
          <input value={note} onChange={e => setNote(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addNote()}
            data-testid="case-note-input"
            placeholder="Add a note…" className={INPUT_CLS} />
          <Button onClick={addNote} className="h-9 px-3 rounded-lg bg-[#8A2BE2]/15 text-[#c39be5] border border-[#8A2BE2]/25 text-[10px] uppercase tracking-[0.05em]">
            <MessageSquare className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {currentUser?.role === 'superadmin' && (
        <div className="pt-3 border-t border-white/[0.06] flex justify-end">
          <Button onClick={doDelete} variant="ghost" size="sm" data-testid="delete-case-btn"
            className="text-red-300/70 hover:text-red-300 hover:bg-red-500/[0.05] text-[10px] uppercase tracking-[0.1em]">
            <Trash2 className="w-3 h-3 mr-1.5" /> Delete case
          </Button>
        </div>
      )}
    </Modal>
  );
};

const InfoRow = ({ label, value, link }) => (
  <div className="flex flex-col">
    <span className="text-[10px] tracking-[0.15em] uppercase text-white/30 mb-0.5">{label}</span>
    {link ? <a href={value} target="_blank" rel="noopener noreferrer" className="text-[#00E5FF]/80 hover:underline break-all">{value}</a> : <span className="text-white/70 break-all">{value}</span>}
  </div>
);

const AccessModal = ({ onClose, authHeaders }) => {
  const [access, setAccess] = useState([]);
  const [newUser, setNewUser] = useState('');
  const [staffList, setStaffList] = useState([]);

  const load = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dmca/access`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/admin/staff-accounts`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      ]);
      setAccess(a.data || []);
      setStaffList(s.data || []);
    } catch { toast.error('Load failed'); }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    if (!newUser.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/dmca/access/grant`, { username: newUser.trim() }, { headers: authHeaders() });
      toast.success(`Granted DMCA access to ${newUser}`);
      setNewUser(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Grant failed'); }
  };
  const revoke = async (u) => {
    if (!window.confirm(`Revoke DMCA access from ${u}?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/dmca/access/revoke/${u}`, {}, { headers: authHeaders() });
      toast.success(`Revoked ${u}`); load();
    } catch { toast.error('Revoke failed'); }
  };

  return (
    <Modal onClose={onClose} title="DMCA Access" subtitle="Grant staff access to the DMCA centre" testid="dmca-access-modal">
      <div className="mb-4">
        <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-2">Grant access</p>
        <div className="flex gap-1.5">
          <input list="staff-list" value={newUser} onChange={e => setNewUser(e.target.value)}
            data-testid="grant-username-input"
            placeholder="Enter staff username" className={INPUT_CLS} />
          <datalist id="staff-list">
            {staffList.map(u => <option key={u.username} value={u.username}>{u.display_name || u.username}</option>)}
          </datalist>
          <Button onClick={grant} data-testid="grant-btn"
            className="h-9 px-3 rounded-lg bg-[#8A2BE2]/15 text-[#c39be5] border border-[#8A2BE2]/25 text-[10px] uppercase tracking-[0.05em]">
            <UserPlus className="w-3 h-3 mr-1" /> Grant
          </Button>
        </div>
      </div>
      <div>
        <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-2">Current team ({access.length})</p>
        {access.length === 0 ? (
          <p className="text-white/30 text-xs text-center py-4">No one else has access. Only superadmins can see the DMCA centre.</p>
        ) : (
          <ul className="space-y-1.5">
            {access.map(a => (
              <li key={a.id} className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                <div>
                  <p className="text-sm text-white">{a.display_name || a.username}</p>
                  <p className="text-[10px] text-white/40">{a.role_label} · granted by {a.granted_by}</p>
                </div>
                <button onClick={() => revoke(a.username)} data-testid={`revoke-${a.username}`}
                  className="text-[10px] text-red-300/70 hover:text-red-300 hover:bg-red-500/[0.06] px-2 py-1 rounded uppercase tracking-[0.05em]">
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

// ─── Templates panel ───
const TemplatesPanel = ({ authHeaders, isSuperadmin }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/dmca/templates`, { headers: authHeaders() });
      setTemplates(r.data || []);
    } catch { toast.error('Load templates failed'); } finally { setLoading(false); }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/dmca/templates/${id}`, { headers: authHeaders() });
      toast.success('Deleted'); load();
    } catch { toast.error('Delete failed'); }
  };

  const copy = (t) => {
    navigator.clipboard.writeText(t.body);
    toast.success(`"${t.name}" copied to clipboard`);
  };

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-[#0A0A12]/60 backdrop-blur-md p-5" data-testid="templates-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium">Reusable letters</p>
          <h2 className="text-lg font-medium text-white tracking-tight">Takedown Templates</h2>
        </div>
        <p className="text-[10px] text-white/40">{templates.length} available</p>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>
      ) : templates.length === 0 ? (
        <p className="text-white/40 text-sm text-center py-6">No templates yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {templates.map(t => (
            <div key={t.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-white text-sm font-medium tracking-tight">{t.name}</h3>
                  <p className="text-[10px] tracking-[0.1em] uppercase text-white/40 mt-0.5">{t.platform}</p>
                </div>
                {isSuperadmin && (
                  <button onClick={() => del(t.id)} className="text-white/30 hover:text-red-300 p-1">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              <p className="text-xs text-white/50 leading-relaxed line-clamp-3 mb-3">{t.body.slice(0, 200)}…</p>
              <div className="flex gap-1.5">
                <Button size="sm" onClick={() => setPreview(t)} data-testid={`view-tpl-${t.id}`}
                  className="h-7 px-3 rounded-full text-[10px] tracking-[0.05em] uppercase bg-white/[0.03] text-white/70 border border-white/[0.1]">
                  Preview
                </Button>
                <Button size="sm" onClick={() => copy(t)} data-testid={`copy-tpl-${t.id}`}
                  className="h-7 px-3 rounded-full text-[10px] tracking-[0.05em] uppercase bg-[#8A2BE2]/15 text-[#c39be5] border border-[#8A2BE2]/25">
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <Modal onClose={() => setPreview(null)} title={preview.name} subtitle="Template preview" testid="tpl-preview-modal">
          <pre className="whitespace-pre-wrap text-xs text-white/70 leading-relaxed font-mono">{preview.body}</pre>
          <div className="flex gap-2 justify-end pt-4 mt-4 border-t border-white/[0.06]">
            <Button onClick={() => copy(preview)} data-testid="copy-preview-btn"
              className="bg-[#8A2BE2]/15 text-[#c39be5] border border-[#8A2BE2]/25 px-4 rounded-full text-[11px] tracking-[0.1em] uppercase">
              <Copy className="w-3 h-3 mr-1.5" /> Copy to clipboard
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Modal = ({ onClose, title, subtitle, children, testid }) => (  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={onClose} data-testid={testid}>
    <div onClick={e => e.stopPropagation()}
      className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0A0A12] border border-white/10 rounded-2xl"
      style={{ boxShadow: '0 0 60px rgba(138,43,226,0.08)' }}>
      <div className="sticky top-0 bg-[#0A0A12]/95 backdrop-blur-md border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
        <div>
          {subtitle && <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-medium">{subtitle}</p>}
          <h2 className="text-lg font-medium text-white tracking-tight">{title}</h2>
        </div>
        <button onClick={onClose} data-testid="modal-close" className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] flex items-center justify-center text-white/60 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

export default DMCAManagementPage;
